/**
 * R69c · BillingDashboard — finance.view-gated aggregated dashboard.
 *
 * Renders four groupings (client · project · assignee · month) with
 * counts and hours totals so finance can see WIP (unbilled), AR
 * (billed but unpaid), and revenue trends at a glance. Reused as:
 *   - Workspace-level page at /app/billing
 *   - Project-scoped panel inside Project Detail → Billing tab
 *   - Portfolio-scoped panel (future — same API accepts filter args)
 *
 * Data: GET /workspaces/{ws}/billing-dashboard?project_id=&client_id=
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Loader2, Building2, Briefcase, User, CalendarDays, DollarSign, Receipt, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BillingDashboard({ wsId, projectId = null, clientId = null, onOpenProject = null, onOpenClient = null, onOpenAssignee = null, dense = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("client");

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = {};
      if (projectId) params.project_id = projectId;
      if (clientId) params.client_id = clientId;
      const { data: d } = await api.get(`/workspaces/${wsId}/billing-dashboard`, { params });
      setData(d);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load billing dashboard"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [wsId, projectId, clientId]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const tabs = useMemo(() => ([
    { id: "client",   label: "By Client",   icon: Building2,  rows: data?.by_client || [],   nameKey: "client_name" },
    { id: "project",  label: "By Project",  icon: Briefcase,  rows: data?.by_project || [],  nameKey: "project_name" },
    { id: "assignee", label: "By Assignee", icon: User,       rows: data?.by_assignee || [], nameKey: "assignee_name" },
    { id: "month",    label: "By Month",    icon: CalendarDays, rows: data?.by_month || [],  nameKey: "month" },
  ]), [data]);

  const activeTab = tabs.find((t) => t.id === tab) || tabs[0];

  if (loading && !data) {
    return (
      <div className="py-16 text-center text-xs text-muted-foreground" data-testid="billing-dashboard-loading">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin opacity-40" />
        Loading billing dashboard…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-10 text-center text-sm text-destructive" data-testid="billing-dashboard-error">
        Couldn&rsquo;t load billing data.
      </div>
    );
  }

  return (
    <div className={dense ? "space-y-3" : "space-y-4"} data-testid="billing-dashboard">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Tile label="Tasks" value={totals.tasks ?? "—"} icon={Receipt} testid="tasks" />
        <Tile label="Total hours" value={totals.billable_hours ?? "—"} icon={DollarSign} accent="text-foreground" testid="total-hours" />
        <Tile label="Billed hours" value={totals.billed_hours ?? "—"} accent="text-sky-700" testid="billed-hours" />
        <Tile label="Paid hours"   value={totals.paid_hours ?? "—"} icon={CheckCircle2} accent="text-emerald-700" testid="paid-hours" />
        <Tile label="Unbilled hrs (WIP)"  value={totals.unbilled_hours ?? "—"} icon={AlertTriangle} accent="text-amber-700" testid="unbilled-hours" />
        <Tile label="Unpaid hrs (AR)"     value={totals.unpaid_hours ?? "—"} accent="text-rose-700" testid="unpaid-hours" />
      </div>

      {/* Group tabs */}
      <div className="flex items-center gap-1 flex-wrap border-b border-border">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === t.id ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`billing-dashboard-tab-${t.id}`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
              <span className="ml-1 text-[10px] text-muted-foreground">({t.rows.length})</span>
            </button>
          );
        })}
      </div>

      {/* Group table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/30 border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 font-medium">{activeTab.label.replace("By ", "")}</th>
              <th className="text-right px-3 py-2 font-medium w-20">Tasks</th>
              <th className="text-right px-3 py-2 font-medium w-24">Total h</th>
              <th className="text-right px-3 py-2 font-medium w-24">Billed h</th>
              <th className="text-right px-3 py-2 font-medium w-24">Unbilled h</th>
              <th className="text-right px-3 py-2 font-medium w-24">Paid h</th>
              <th className="text-right px-3 py-2 font-medium w-24">Unpaid h</th>
              <th className="text-right px-3 py-2 font-medium w-32">Billed / Total</th>
            </tr>
          </thead>
          <tbody>
            {activeTab.rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-10 text-muted-foreground italic">
                  No billable tasks in this scope yet.
                </td>
              </tr>
            ) : activeTab.rows.map((row) => {
              const total = row.billable_hours || 0;
              const pct = total > 0 ? Math.round((row.billed_hours / total) * 100) : 0;
              return (
                <tr key={row.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`billing-dashboard-row-${row.id}`}>
                  <td className="px-3 py-1.5">
                    {activeTab.id === "project" && onOpenProject && row.id !== "_none" ? (
                      <button type="button" onClick={() => onOpenProject(row.id)} className="text-left hover:text-primary underline-offset-2 hover:underline" data-testid={`billing-open-project-${row.id}`}>
                        {row[activeTab.nameKey] || "—"}
                      </button>
                    ) : activeTab.id === "client" && onOpenClient && row.id !== "_none" ? (
                      <button type="button" onClick={() => onOpenClient(row.id)} className="text-left hover:text-primary underline-offset-2 hover:underline" data-testid={`billing-open-client-${row.id}`}>
                        {row[activeTab.nameKey] || "—"}
                      </button>
                    ) : activeTab.id === "assignee" && onOpenAssignee && row.id !== "_none" ? (
                      <button type="button" onClick={() => onOpenAssignee(row.id, row[activeTab.nameKey])} className="text-left hover:text-primary underline-offset-2 hover:underline" data-testid={`billing-open-assignee-${row.id}`}>
                        {row[activeTab.nameKey] || "—"}
                      </button>
                    ) : (
                      <span>{row[activeTab.nameKey] || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">{row.tasks}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{row.billable_hours}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-sky-700">{row.billed_hours}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-amber-700">{row.unbilled_hours}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{row.paid_hours}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-rose-700">{row.unpaid_hours}</td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-16 h-1.5 bg-muted overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <span className="text-[10px] font-mono w-8 text-right">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent = "text-foreground", testid = null }) {
  return (
    <div className="border border-border bg-card p-2" data-testid={testid ? `billing-dashboard-tile-${testid}` : undefined}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-lg font-bold ${accent}`}>{value}</div>
    </div>
  );
}
